import React, { useState } from "react";
import toast from "../ui/toast";
import "./FormMoreInfo.css";
import useLang from "../../i18n/useLang";

const CONTENT = {
  fr: {
    defaultSubmit: "Voir les résultats",
    errFirstName: "Veuillez entrer votre prénom",
    errLastName: "Veuillez entrer votre nom",
    errEmail: "Veuillez indiquer votre email",
    errEmailInvalid: "Email non valide",
    errCompany: "Veuillez indiquer votre entreprise",
    errNbBusinesses: "Indiquez le nombre d'établissements gérés.",
    errNbBusinessesInvalid: "Indiquez un nombre valide",
    blocked: "Vous n'avez plus de crédit. Contactez-nous pour obtenir un audit.",
    saveError: "Erreur lors de la sauvegarde",
    subtitle: "Renseignez vos coordonnées pour accéder aux résultats.",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Email professionnel",
    company: "Entreprise",
    nbBusinesses: "Nb. établissements",
    loading: "Chargement...",
    fixErrors: "Veuillez corriger les champs en rouge.",
    privacy: "Vos données restent confidentielles et ne seront jamais partagées.",
  },
  en: {
    defaultSubmit: "See results",
    errFirstName: "Please enter your first name",
    errLastName: "Please enter your last name",
    errEmail: "Please enter your email",
    errEmailInvalid: "Invalid email address",
    errCompany: "Please enter your company name",
    errNbBusinesses: "Enter the number of locations you manage.",
    errNbBusinessesInvalid: "Enter a valid number",
    blocked: "You have no credits left. Contact us to get an audit.",
    saveError: "Something went wrong, please try again",
    subtitle: "Enter your details to see your results.",
    firstName: "First name",
    lastName: "Last name",
    email: "Work email",
    company: "Company",
    nbBusinesses: "No. of locations",
    loading: "Loading...",
    fixErrors: "Please fix the fields in red.",
    privacy: "Your data stays private and is never shared.",
  },
};

const BLOCKED_EMAILS = ["leroymerlin", "rinkas@hotmail.fr"];
const BLOCKED_COMPANIES = ["compardemenage.fr"];

const FormMoreInfo = ({ onSubmit: onSubmitProp, submitLabel }) => {
  const t = CONTENT[useLang()];
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    nbBusinesses: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!values.firstName) errs.firstName = t.errFirstName;
    if (!values.lastName) errs.lastName = t.errLastName;
    if (!values.email) errs.email = t.errEmail;
    else if (!/\S+@\S+\.\S+/.test(values.email))
      errs.email = t.errEmailInvalid;
    if (!values.company) errs.company = t.errCompany;
    if (!values.nbBusinesses)
      errs.nbBusinesses = t.errNbBusinesses;
    else if (isNaN(Number(values.nbBusinesses)))
      errs.nbBusinesses = t.errNbBusinessesInvalid;
    return errs;
  };

  const isBlocked = () => {
    if (BLOCKED_EMAILS.some((b) => values.email.includes(b))) return true;
    if (BLOCKED_COMPANIES.some((b) => values.company.includes(b))) return true;
    return false;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    if (isBlocked()) {
      toast.warning(t.blocked);
      return;
    }

    setLoading(true);
    try {
      const data = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        company: values.company,
        nbBusinesses: Number(values.nbBusinesses),
      };
      await onSubmitProp(data);
    } catch (error) {
      console.log(error);
      toast.error(t.saveError);
    } finally {
      setLoading(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <form className="info-form" onSubmit={onSubmit}>
      <p className="info-form__subtitle">{t.subtitle}</p>

      <div className="info-form__row">
        <div className="info-form__field">
          <input
            className={`input${errors.firstName ? " input--error" : ""}`}
            placeholder={t.firstName}
            autoFocus
            value={values.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
          />
          {errors.firstName && (
            <span className="info-form__error">{errors.firstName}</span>
          )}
        </div>
        <div className="info-form__field">
          <input
            className={`input${errors.lastName ? " input--error" : ""}`}
            placeholder={t.lastName}
            value={values.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
          />
          {errors.lastName && (
            <span className="info-form__error">{errors.lastName}</span>
          )}
        </div>
      </div>

      <div className="info-form__field">
        <input
          className={`input${errors.email ? " input--error" : ""}`}
          placeholder={t.email}
          type="email"
          value={values.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
        {errors.email && (
          <span className="info-form__error">{errors.email}</span>
        )}
      </div>

      <div className="info-form__row">
        <div className="info-form__field info-form__field--grow">
          <input
            className={`input${errors.company ? " input--error" : ""}`}
            placeholder={t.company}
            value={values.company}
            onChange={(e) => handleChange("company", e.target.value)}
          />
          {errors.company && (
            <span className="info-form__error">{errors.company}</span>
          )}
        </div>
        <div className="info-form__field info-form__field--small">
          <input
            className={`input${errors.nbBusinesses ? " input--error" : ""}`}
            placeholder={t.nbBusinesses}
            type="number"
            min={1}
            max={100000}
            value={values.nbBusinesses}
            onChange={(e) => handleChange("nbBusinesses", e.target.value)}
          />
          {errors.nbBusinesses && (
            <span className="info-form__error">{errors.nbBusinesses}</span>
          )}
        </div>
      </div>

      <button
        className={`btn btn--primary btn--block info-form__submit${loading ? " btn--loading" : ""}`}
        disabled={loading}
        type="submit"
      >
        {loading ? t.loading : submitLabel || t.defaultSubmit}
      </button>

      {hasErrors && (
        <p className="info-form__hint">{t.fixErrors}</p>
      )}

      <p className="info-form__footer">{t.privacy}</p>
    </form>
  );
};

export default FormMoreInfo;
